import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Mic,
  CheckCircle,
  Cancel,
  PlayArrow,
  Person,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import SocketHandler from "../../../utils/socketHandler";
import { useAuth } from "../../../hooks";
import {
  useAcceptPerformerInvitation,
  useRejectPerformerInvitation,
  useCheckInviteStatus,
} from "../../../services/stagemode_api/moderator/moderator_api";
import { useErrorResponse } from "../../../hooks/useErrorResponse";
import { useNavigate, useParams } from "react-router-dom";
import { ensureInviteSocket, onPerformerInvite } from "../../../utils/stageInviteBus";

// Styled dark card section - consistent with QuickReactions
const InteractionSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.6)",
  fontFamily: "Lexend, sans-serif",
  color: "#fff",
}));

// Styled action buttons
const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 120,
  height: 48,
  borderRadius: "24px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.3)",
  },
}));

// Styled popup dialog
const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    backgroundColor: "#1A1A1A",
    borderRadius: "16px",
    color: "#fff",
    fontFamily: "Lexend, sans-serif",
    minWidth: 400,
    maxWidth: 500,
  },
}));

// Types for performer invitation
export type PerformerInvitationStatus = "Pending" | "Accepted" | "Rejected";

export interface PerformerInvitationProps {
  status?: PerformerInvitationStatus;
  performerName?: string;
  bookingId: string;
}

const PerformerInvitation: React.FC<PerformerInvitationProps> = ({
  status = "Pending",
  performerName = "You",
  bookingId,
}) => {
  const [currentStatus, setCurrentStatus] =
    useState<PerformerInvitationStatus>(status);
  const [isInvited, setIsInvited] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const socketSubscribedRef = useRef(false);
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const {
    update: acceptPerformerInvitation,
    error: acceptPerformerInvitationError,
  } = useAcceptPerformerInvitation({
    key: ["accept-performer-invitation"],
    params: {
      id: bookingId,
      userId: userInfo?.id ?? "",
    },
  });
  const { channel_name } = useParams();

  const {
    update: rejectPerformerInvitation,
    error: rejectPerformerInvitationError,
  } = useRejectPerformerInvitation({
    key: ["reject-performer-invitation"],
    params: {
      id: bookingId,
      userId: userInfo?.id ?? "",
    },
  });

  const { update: checkInviteStatus, error: checkInviteStatusError } =
    useCheckInviteStatus({
      key: ["check-invite-status"],
      params: {
        id: bookingId,
        userId: userInfo?.id ?? "",
      },
    });
  useErrorResponse(
    acceptPerformerInvitationError ||
      rejectPerformerInvitationError ||
      checkInviteStatusError
  );

  const handleAccept = async () => {
    try {
      await acceptPerformerInvitation(
        {},
        {
          onSuccess: () => {
            setCurrentStatus("Accepted");
            setShowPopup(false);
            setIsInvited(true);
          },
        }
      );
    } catch (error) {
      console.error("Failed to accept invitation:", error);
    }
  };

  const handleReject = async () => {
    try {
      await rejectPerformerInvitation(
        {},
        {
          onSuccess: () => {
            setCurrentStatus("Rejected");
            setShowPopup(false);
            setIsInvited(true);
          },
        }
      );
    } catch (error) {
      console.error("Failed to reject invitation:", error);
    }
  };

  const handleCheckInviteStatus = async () => {
    try {
      await checkInviteStatus(
        {},
        {
          onSuccess: (data) => {
            console.log("Invitation status:", data);
            setCurrentStatus(data.data.status);
            setShowPopup(false);
            if (
              data.data.status === "Accepted" ||
              data.data.status === "Rejected"
            ) {
              setIsInvited(true);
            }
          },
        }
      );
    } catch (error) {
      console.error("Failed to check invite status:", error);
    }
  };

  const handleStartPerformance = () => {
    navigate(`/stage-live-performance/${channel_name}?bookingId=${bookingId}`);
  };

 // put ref as a key so we can safely re-subscribe if booking/user changes
const subscriptionKeyRef = useRef<string | null>(null);

useEffect(() => {
  const uid = userInfo?.id;
  if (!bookingId || !uid) return;

  ensureInviteSocket(bookingId, uid);

  const off = onPerformerInvite((data) => {
    // you can also set isInvited / status here if you want
    setShowPopup(true);
  });

  return () => off();
}, [bookingId, userInfo?.id]);

// keep your status check
useEffect(() => {
  handleCheckInviteStatus();
}, []);


  // Popup dialog for invitation
  const renderInvitationPopup = () => (
    <StyledDialog
      open={showPopup}
      onClose={() => setShowPopup(false)}
      aria-labelledby="invitation-dialog-title"
      aria-describedby="invitation-dialog-description"
    >
      <DialogTitle id="invitation-dialog-title">
        <Box display="flex" alignItems="center">
          <Mic sx={{ mr: 1, color: "#e50914" }} />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Lexend",
              fontWeight: 600,
              fontSize: "20px",
              color: "#fff",
            }}
          >
            Performance Invitation
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            fontFamily: "Lexend",
            color: "#fff",
            textAlign: "center",
            mb: 2,
          }}
        >
          You have been invited to perform!
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#999",
            textAlign: "center",
            fontFamily: "Lexend",
          }}
        >
          Would you like to accept this performance invitation?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "center" }}>
        <Stack direction="row" spacing={2}>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={handleAccept}
              sx={{
                background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
                color: "#fff",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)",
                },
              }}
            >
              Accept
            </ActionButton>
          </motion.div>

          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ActionButton
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleReject}
              sx={{
                borderColor: "#EF5350",
                color: "#EF5350",
                "&:hover": {
                  borderColor: "#D32F2F",
                  backgroundColor: "rgba(239, 83, 80, 0.1)",
                },
              }}
            >
              Reject
            </ActionButton>
          </motion.div>
        </Stack>
      </DialogActions>
    </StyledDialog>
  );

  const getStatusChip = () => {
    const statusConfig = {
      Pending: {
        label: "Pending",
        color: "#FFA726",
        backgroundColor: "rgba(255, 167, 38, 0.1)",
      },
      Accepted: {
        label: "Accepted",
        color: "#66BB6A",
        backgroundColor: "rgba(102, 187, 106, 0.1)",
      },
      Rejected: {
        label: "Rejected",
        color: "#EF5350",
        backgroundColor: "rgba(239, 83, 80, 0.1)",
      },
    };

    const config = statusConfig[currentStatus];

    return (
      <Chip
        icon={<Person />}
        label={config.label}
        sx={{
          color: config.color,
          backgroundColor: config.backgroundColor,
          border: `1px solid ${config.color}`,
          fontWeight: 600,
          fontSize: "12px",
        }}
      />
    );
  };

  const renderPendingActions = () => (
    <Stack direction="row" spacing={2} justifyContent="center">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="contained"
          startIcon={<CheckCircle />}
          onClick={handleAccept}
          sx={{
            background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #388E3C 0%, #4CAF50 100%)",
            },
          }}
        >
          Accept
        </ActionButton>
      </motion.div>

      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="outlined"
          startIcon={<Cancel />}
          onClick={handleReject}
          sx={{
            borderColor: "#EF5350",
            color: "#EF5350",
            "&:hover": {
              borderColor: "#D32F2F",
              backgroundColor: "rgba(239, 83, 80, 0.1)",
            },
          }}
        >
          Reject
        </ActionButton>
      </motion.div>
    </Stack>
  );

  const renderAcceptedActions = () => (
    <Stack direction="row" spacing={2} justifyContent="center">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <ActionButton
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={handleStartPerformance}
          sx={{
            background: "linear-gradient(135deg, #e50914 0%, #ff1744 100%)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(135deg, #c62828 0%, #e50914 100%)",
            },
          }}
        >
          Start Performance
        </ActionButton>
      </motion.div>
    </Stack>
  );

  const renderRejectedState = () => (
    <Box textAlign="center">
      <Typography
        variant="body2"
        sx={{
          color: "#999",
          fontStyle: "italic",
          fontFamily: "Lexend, sans-serif",
        }}
      >
        Performance invitation was declined
      </Typography>
    </Box>
  );

  return (
    <>
      {renderInvitationPopup()}

      {isInvited && (
        <Grid item xs={12}>
          <InteractionSection>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box display="flex" alignItems="center">
                <Mic sx={{ mr: 1, color: "#e50914" }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Lexend",
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#fff",
                  }}
                >
                  Performance Invitation
                </Typography>
              </Box>
              {getStatusChip()}
            </Box>

            <Box mb={3}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Lexend",
                  color: "#fff",
                  textAlign: "center",
                  mb: 1,
                }}
              >
                {currentStatus === "Pending" &&
                  `${performerName} has been invited to perform!`}
                {currentStatus === "Accepted" &&
                  `${performerName} is ready to perform!`}
                {currentStatus === "Rejected" &&
                  `${performerName} declined the performance invitation`}
              </Typography>

              {currentStatus === "Pending" && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    textAlign: "center",
                    fontFamily: "Lexend",
                  }}
                >
                  Please accept or reject this invitation to proceed
                </Typography>
              )}

              {currentStatus === "Accepted" && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#999",
                    textAlign: "center",
                    fontFamily: "Lexend",
                  }}
                >
                  Click "Start Performance" when you're ready to begin
                </Typography>
              )}
            </Box>

            {currentStatus === "Pending" && renderPendingActions()}
            {currentStatus === "Accepted" && renderAcceptedActions()}
            {currentStatus === "Rejected" && renderRejectedState()}
          </InteractionSection>
        </Grid>
      )}
    </>
  );
};

export default PerformerInvitation;